import React, { useEffect } from 'react';
import { RadioGroup, RadioGroupItem } from '@accure/ui';

import { Tooltip } from '@/components/ui/tooltip';
import { GetLevels } from '@/data-explorer-v2/api/queries/get-levels';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useMe } from '@/hooks/use-me';
import { cls } from '@/utils/cls';

export type Domain = 'battery' | 'pcs';

type DomainToggleProps = {
  domain: Domain;
  onDomainChange: (d: Domain) => void;
};

const radioItemStyles = cls(
  'hover:bg-primary-50/30 hover:border-primary-300',
  'data-[state=checked]:border-primary-500 data-[state=checked]:bg-primary-100',
  'focus-visible:ring-primary-500/30 focus-visible:border-primary-600',
  'transition-all duration-200',
  '[&_[data-slot="radio-group-indicator"]_svg]:fill-primary-600',
  '[&_[data-slot="radio-group-indicator"]_svg]:text-primary-600',
);

const disabledRadioItemStyles = cls(
  'cursor-not-allowed opacity-40',
  'border-gray-300 hover:bg-transparent hover:border-gray-300',
);

export const DomainToggle: React.FC<DomainToggleProps> = ({
  domain,
  onDomainChange,
}) => {
  const { config } = useMe();
  const isBess = config.type === 'bess';

  const featureFlag = useFeatureFlag();
  const getLevelsData = GetLevels.useQuery({ retry: 3 });

  const hasPcsData = Boolean(getLevelsData.data?.pcs);
  const isPcsDisabled = !hasPcsData;

  // Hide toggle entirely for non-BESS or when PCS feature flag is off
  const shouldShowToggle = isBess && featureFlag('pcs');

  // Auto-reset to battery when PCS data is not available for current site
  useEffect(() => {
    if (!getLevelsData.isLoading && !hasPcsData && domain === 'pcs') {
      onDomainChange('battery');
    }
  }, [getLevelsData.isLoading, hasPcsData, domain, onDomainChange]);

  if (!shouldShowToggle) {
    return null;
  }

  return (
    <div className="flex flex-row justify-center gap-4">
      <RadioGroup value={domain} onValueChange={onDomainChange}>
        <div className="flex flex-col justify-center gap-2">
          <label className="group flex cursor-pointer items-center gap-2">
            <RadioGroupItem value="battery" className={radioItemStyles} />
            <span className="text-sm transition-colors duration-200 group-hover:text-primary-800">
              Battery
            </span>
          </label>

          <Tooltip
            content="No PCS data available for this site"
            hide={!isPcsDisabled}
            position="right"
          >
            <label
              className={cls('group flex items-center gap-2', {
                'cursor-pointer': !isPcsDisabled,
                'cursor-not-allowed': isPcsDisabled,
              })}
            >
              <RadioGroupItem
                value="pcs"
                disabled={isPcsDisabled}
                className={cls(radioItemStyles, {
                  [disabledRadioItemStyles]: isPcsDisabled,
                })}
              />
              <span
                className={cls('text-sm transition-colors duration-200', {
                  'group-hover:text-primary-800': !isPcsDisabled,
                  'text-gray-400': isPcsDisabled,
                })}
              >
                PCS
              </span>
            </label>
          </Tooltip>
        </div>
      </RadioGroup>
    </div>
  );
};
